import ITKHelper from 'vtk.js/Sources/Common/DataModel/ITKHelper';
import readImageDICOMFileSeries from 'itk/readImageDICOMFileSeries';
import vtkITKImageReader from 'vtk.js/Sources/IO/Misc/ITKImageReader';
import readImageArrayBuffer from 'itk/readImageArrayBuffer';
import readImageFile from 'itk/readImageFile';
import vtkDataArray from 'vtk.js/Sources/Common/Core/DataArray';
const { convertItkToVtkImage } = ITKHelper;

import {
    isNullOrUndefined,
} from './Common';

vtkITKImageReader.setReadImageArrayBufferFromITK(readImageArrayBuffer);

function fixScalars(vtkImage) {
    const oldScalars = vtkImage.getPointData().getScalars().getData();
    if (oldScalars instanceof Uint8Array) {
        return;
    }

    const maskData = new Uint8Array(oldScalars.length);

    for (let i = 0; i < oldScalars.length; i++) {
        maskData[i] = oldScalars[i];
    }

    const newScalars = vtkDataArray.newInstance({
        numberOfComponents: 1,
        values: maskData,
    });

    vtkImage.getPointData().setScalars(newScalars);
}

function storeImage(cache) {
    return (itkImage, imageFiles) => {
        const vtkImage = convertItkToVtkImage(itkImage);
        cache.set(imageFiles, vtkImage);
        return vtkImage;
    };
}

function storeMask(maskCache, baselineCache) {
    return (itkImage, imageFiles) => {
        const vtkImage = convertItkToVtkImage(itkImage);
        if (!isNullOrUndefined(vtkImage)) {
            fixScalars(vtkImage);
        }

        maskCache.set(imageFiles, vtkImage);

        const baselineScalars = vtkImage.getPointData().getScalars().newClone();
        baselineScalars.deepCopy(vtkImage.getPointData().getScalars());
        baselineCache.set(imageFiles, baselineScalars);

        return vtkImage;
    };
}

class ImageLoader {
    constructor() {
        this.dicomImageCache = new Map();
        this.dicomMaskCache = new Map();
        this.dicomMaskBaselineCache = new Map();
        this.nrrdImageCache = new Map();
        this.nrrdMaskCache = new Map();
        this.nrrdMaskBaselineCache = new Map();
    }

    #isNrrdFile(imageFiles) {
        return imageFiles.name.endsWith('.nrrd');
    }

    #allNrrdFiles(imageFiles) {
        if (isNullOrUndefined(imageFiles) || !imageFiles.length) return false;
        return Array.from(imageFiles).every(f => this.#isNrrdFile(f));
    }

    #allDicomFiles(imageFiles) {
        if (isNullOrUndefined(imageFiles) || !imageFiles.length) return false;
        return Array.from(imageFiles).every(f => !this.#isNrrdFile(f));
    }

    #tryGetDicom(imageFiles, cache, store) {
        // nothing to load for empty
        if (isNullOrUndefined(imageFiles) || !imageFiles.length) {
            return Promise.resolve(null);
        }
        let entry = cache.get(imageFiles);

        if (!entry) {
            entry = readImageDICOMFileSeries(imageFiles)
                .then(function ({image}) {
                    return image;
                })
                .then(function (image) {
                    return store(image, imageFiles);
                });
            cache.set(imageFiles, entry);
        }

        return Promise.resolve(entry);
    }

    #tryGetNrrd(imageFiles, cache, store) {

        if (isNullOrUndefined(imageFiles) || !imageFiles || imageFiles.length === 0) {
            return Promise.resolve(null);
        }

        const nrrdFile = imageFiles[0];

        let entry = cache.get(nrrdFile);

        if (!entry) {
            entry = readImageFile(null, nrrdFile)
                .then(({ image }) => {
                    return store(image, nrrdFile);
                });
            cache.set(nrrdFile, entry);
        }
        return Promise.resolve(entry);
    }

    tryGetImage(imageFiles) {
        if (isNullOrUndefined(imageFiles) || !imageFiles || imageFiles.length === 0) {
            return Promise.resolve(null);
        }

        if (this.#allNrrdFiles(imageFiles)) {
            return this.#tryGetNrrd(imageFiles, this.nrrdImageCache, storeImage(this.nrrdImageCache));
        } else if (this.#allDicomFiles(imageFiles)) {
            return this.#tryGetDicom(imageFiles, this.dicomImageCache, storeImage(this.dicomImageCache));
        }
        return Promise.resolve(null);
    }

    tryGetMask(imageFiles) {

        if (isNullOrUndefined(imageFiles) || !imageFiles.length) {
            return Promise.resolve(null);
        }

        if (this.#allNrrdFiles(imageFiles)) {
            return this.#tryGetNrrd(imageFiles, this.nrrdMaskCache, storeMask(this.nrrdMaskCache, this.nrrdMaskBaselineCache));
        } else if (this.#allDicomFiles(imageFiles)) {
            return this.#tryGetDicom(imageFiles, this.dicomMaskCache, storeMask(this.dicomMaskCache, this.dicomMaskBaselineCache));
        }
        
        return Promise.resolve(null);
    }

    tryGetMaskBaseline(imageFiles) {
        if (isNullOrUndefined(imageFiles) || !imageFiles.length) {
            return null;
        }

        if (this.#allNrrdFiles(imageFiles)) {
            const nrrdFile = imageFiles[0];
            return this.nrrdMaskBaselineCache.get(nrrdFile);
        }
        return this.dicomMaskBaselineCache.get(imageFiles);
    }
}

export function mkImageLoader() {
    return new ImageLoader();
}
